(() => {
  'use strict';
  const drawer = document.getElementById('cocon-cart-drawer');
  if (!drawer) return;
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const route = path => root + path.replace(/^\//, '');
  const sectionId = 'cocon-cart-drawer';
  let opener = null;
  let revision = 0;
  let pending = null;
  let changing = false;
  let rendered = false;
  let complementDismissed = false;

  function error(message = '') {
    const node = drawer.querySelector('[data-cocon-drawer-error]');
    if (node) { node.textContent = message; node.hidden = !message; }
  }

  function busy(value) {
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-cocon-drawer-key],[data-cocon-drawer-remove],[data-cocon-complement-add],[data-cocon-complement-variant],[name="checkout"]').forEach(button => button.disabled = value);
    const wallets = drawer.querySelector('[data-cocon-drawer-payments]');
    if (wallets) wallets.inert = value;
    const status = drawer.querySelector('[data-cocon-drawer-loading]');
    if (status) status.hidden = !value;
  }

  function replaceSection(html) {
    if (!html) return false;
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cocon-drawer-content]');
    const current = drawer.querySelector('[data-cocon-drawer-content]');
    if (!incoming || !current) return false;
    const active = document.activeElement;
    const key = active?.dataset.coconDrawerKey || active?.dataset.coconDrawerRemove;
    const delta = active?.dataset.coconDrawerDelta;
    const closeFocused = active?.hasAttribute('data-cocon-drawer-close');
    const scrollTop = drawer.querySelector('[data-cocon-drawer-scroll]')?.scrollTop || 0;
    current.replaceWith(document.importNode(incoming, true));
    const scroll = drawer.querySelector('[data-cocon-drawer-scroll]');
    if (scroll) scroll.scrollTop = scrollTop;
    if (drawer.open && (key || closeFocused)) {
      const next = [...drawer.querySelectorAll('[data-cocon-drawer-key],[data-cocon-drawer-remove]')].find(node => (node.dataset.coconDrawerKey || node.dataset.coconDrawerRemove) === key && node.dataset.coconDrawerDelta === delta);
      (next || drawer.querySelector('[data-cocon-drawer-close]'))?.focus({ preventScroll: true });
    }
    if (complementDismissed) drawer.querySelector('[data-cocon-complement]')?.setAttribute('hidden', '');
    rendered = true;
    return true;
  }

  async function read({ announce = false } = {}) {
    const version = ++revision;
    pending?.abort();
    pending = new AbortController();
    const signal = pending.signal;
    busy(true);
    error();
    try {
      const [cartResponse, sectionResponse] = await Promise.all([
        fetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal }),
        fetch(route('cart') + '?sections=' + sectionId, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal })
      ]);
      if (!cartResponse.ok || !sectionResponse.ok) throw new Error('Le panier ne peut pas être chargé pour le moment. Réessayez ou ouvrez la page panier.');
      const [cart, sections] = await Promise.all([cartResponse.json(), sectionResponse.json()]);
      if (version !== revision) return;
      if (!replaceSection(sections[sectionId])) throw new Error('Le panier ne peut pas être affiché pour le moment. Ouvrez la page panier pour continuer.');
      document.dispatchEvent(new CustomEvent('cocon:cart-updated', { detail: { cart, fromDrawer: true } }));
      if (announce) {
        const status = drawer.querySelector('[data-cocon-drawer-announcement]');
        if (status) status.textContent = 'Panier mis à jour.';
      }
    } catch (failure) {
      if (failure.name !== 'AbortError' && version === revision) error(failure.message);
    } finally {
      if (version === revision) { busy(false); pending = null; }
    }
  }

  function open(trigger, refresh = true) {
    if (!drawer.open) {
      document.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      opener = trigger instanceof HTMLElement ? trigger : document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('cocon-drawer-open');
      document.body.classList.add('cocon-drawer-open');
      drawer.querySelector('[data-cocon-drawer-close]')?.focus({ preventScroll: true });
    }
    if (refresh && !changing) read();
  }

  async function change(button) {
    if (changing) return;
    const key = button.dataset.coconDrawerRemove || button.dataset.coconDrawerKey;
    const line = button.closest('[data-cocon-drawer-line]');
    const quantity = button.dataset.coconDrawerRemove ? 0 : Math.max(0, Number(line?.dataset.quantity || 0) + Number(button.dataset.coconDrawerDelta || 0));
    if (!key || !Number.isInteger(quantity)) return;
    changing = true;
    ++revision;
    pending?.abort();
    pending = null;
    busy(true);
    error();
    let committed = false;
    try {
      const response = await fetch(route('cart/change.js'), {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [sectionId], sections_url: route('cart') })
      });
      const cart = await response.json();
      if (!response.ok || cart.status >= 400) throw new Error(cart.description || cart.message || 'La quantité ne peut pas être modifiée. Réessayez.');
      committed = true;
      const replaced = replaceSection(cart.sections?.[sectionId]);
      document.dispatchEvent(new CustomEvent('cocon:cart-updated', { detail: { cart, fromDrawer: true } }));
      if (!replaced) await read({ announce: true });
      const status = drawer.querySelector('[data-cocon-drawer-announcement]');
      if (status) status.textContent = quantity ? 'Quantité mise à jour.' : 'Article retiré du panier.';
    } catch (failure) {
      if (!committed) {
        await read();
        error(failure.message || 'Le panier ne peut pas être modifié pour le moment.');
      } else error('Panier mis à jour. Ouvrez la page panier pour continuer.');
    } finally {
      changing = false;
      busy(false);
    }
  }


  function complementData(card) {
    try { return JSON.parse(card.querySelector('[data-cocon-complement-data]').textContent); }
    catch { return null; }
  }

  function selectComplement(select) {
    const card = select.closest('[data-cocon-complement]');
    const data = card && complementData(card);
    const variant = data?.product.variants.find(item => String(item.id) === select.value);
    if (!variant || !variant.available || variant.maxQuantity === 0) return;
    const url = data.url + '?variant=' + encodeURIComponent(variant.id);
    card.querySelectorAll('.complement-image,.complement-title').forEach(link => link.href = url);
    const image = card.querySelector('.complement-image img');
    if (image) { image.src = variant.image || data.product.image; image.removeAttribute('srcset'); }
    const money = value => new Intl.NumberFormat('fr-FR', {style:'currency',currency:'EUR'}).format(value / 100);
    const price = card.querySelector('[data-cocon-complement-price]');
    price.replaceChildren();
    const current = document.createElement('strong');
    current.textContent = money(variant.priceCents);
    price.append(current);
    if (variant.compareAtCents > variant.priceCents) {
      const previous = document.createElement('s'); previous.textContent = money(variant.compareAtCents);
      const badge = document.createElement('span'); badge.className = 'discount-pill'; badge.textContent = '−20 %';
      price.append(previous, badge);
    }
    const button = card.querySelector('[data-cocon-complement-add]');
    button.dataset.coconComplementAdd = String(variant.id);
    button.setAttribute('aria-label', 'Ajouter ' + data.product.shortTitle + (variant.color ? ', ' + variant.color : ''));
  }

  function complementStatus(message) {
    const node = drawer.querySelector('[data-cocon-complement-status]');
    if (node) { node.querySelector('span').textContent = message; node.hidden = !message; }
    const announcement = drawer.querySelector('[data-cocon-drawer-announcement]');
    if (announcement) announcement.textContent = message;
  }

  async function addComplement(button) {
    if (changing || drawer.getAttribute('aria-busy') === 'true') return;
    const card = button.closest('[data-cocon-complement]');
    const data = card && complementData(card);
    const id = button.dataset.coconComplementAdd;
    const variant = data?.product.variants.find(item => String(item.id) === id);
    if (!variant || !variant.available || variant.maxQuantity === 0) return;
    changing = true;
    ++revision;
    pending?.abort(); pending = null;
    busy(true); error();
    let committed = false;
    try {
      const beforeResponse = await fetch(route('cart.js'), {credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
      if (!beforeResponse.ok) throw new Error('Le panier ne peut pas être vérifié. Réessayez.');
      const before = await beforeResponse.json();
      if (before.items.some(item => item.handle === data.product.handle)) {
        complementDismissed = true;
        await read();
        complementStatus(data.product.shortTitle + ' est déjà dans votre panier.');
        return;
      }
      const response = await fetch(route('cart/add.js'), {
        method:'POST', credentials:'same-origin', cache:'no-store',
        headers:{Accept:'application/json','Content-Type':'application/json'},
        body:JSON.stringify({items:[{id:Number(id),quantity:1}],sections:[sectionId],sections_url:route('cart')})
      });
      const added = await response.json();
      if (!response.ok || added.status >= 400) throw new Error(added.description || added.message || 'Ce complément ne peut pas être ajouté. Réessayez.');
      committed = true;
      complementDismissed = true;
      const replaced = replaceSection(added.sections?.[sectionId]);
      const cartResponse = await fetch(route('cart.js'), {credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
      if (!cartResponse.ok) throw new Error('Article ajouté. Ouvrez la page panier pour retrouver votre total.');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cocon:cart-updated', {detail:{cart,fromDrawer:true}}));
      if (!replaced) await read();
      complementStatus(data.product.shortTitle + ' ajouté au panier.');
      const status = drawer.querySelector('[data-cocon-complement-status]');
      if (status && document.activeElement === document.body) { status.setAttribute('tabindex','-1'); status.focus({preventScroll:true}); }
    } catch (failure) {
      await read();
      error(failure.message || (committed ? 'Article ajouté. Ouvrez la page panier pour continuer.' : 'Le complément ne peut pas être ajouté pour le moment.'));
    } finally { changing = false; busy(false); }
  }

  document.addEventListener('change', event => {
    if (event.target.matches('[data-cocon-complement-variant]') && drawer.contains(event.target)) selectComplement(event.target);
  });

  document.addEventListener('cocon:cart-updated', event => {
    if (event.detail?.fromDrawer) return;
    complementDismissed = false;
    ++revision;
    pending?.abort();
    pending = null;
    if (replaceSection(event.detail?.sections?.[sectionId])) busy(false);
    else { rendered = false; if (drawer.open && !changing) read(); }
  });
  document.addEventListener('cocon:cart-open', event => open(event.detail?.trigger, !rendered));
  document.addEventListener('click', event => {
    const button = event.target.closest('a,button');
    if (!button) return;
    if (button.matches('[data-cocon-cart-open],.header-actions a[href]') && (button.hasAttribute('data-cocon-cart-open') || new URL(button.href, location.href).pathname === route('cart'))) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open(button);
      return;
    }
    if (!drawer.contains(button)) return;
    if (button.hasAttribute('data-cocon-complement-add')) { event.preventDefault(); addComplement(button); return; }
    if (button.hasAttribute('data-cocon-drawer-close')) { drawer.close(); return; }
    if (button.hasAttribute('data-cocon-drawer-key') || button.hasAttribute('data-cocon-drawer-remove')) {
      event.preventDefault();
      change(button);
    }
  });
  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const rect = drawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) drawer.close();
  });
  drawer.addEventListener('close', () => {
    document.documentElement.classList.remove('cocon-drawer-open');
    document.body.classList.remove('cocon-drawer-open');
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
})();
